/**
 * All the lifecycle methods in this file would only run
 * in case of stanalone app.
 * Note: They won't run when blink-v2 is used as a library
 * inside another app.
 */

import {
    fetchAndRegisterI18nTranslations,
    initializeAppLocale,
    initializeChromiumTranslate,
    overrideStrings,
    setStringOverrides,
    setTranslationIDOverrides,
} from '@thoughtspot/i18n';
import { initLogger } from '@thoughtspot/logger';
import { logMicroFrontendsDebugInfo } from '@thoughtspot/microfrontends';
import {
    getFormatLocales,
    getLiveboardVizDataCacheConfig,
    getMuzeChartIdbCacheEnabled,
    getPreferredCurrencyFormat,
    isAutomaticTranslation,
    isManualTranslation,
    markManuallyLoggedOut,
    onUserLoggedIn,
    onUserLoggedOut,
} from '@thoughtspot/session-service';
import {
    isLocalizationEnabled,
    isMixpanelEnabled,
    setMixpanelConfig,
} from '@thoughtspot/system-config-service';
import _ from 'lodash';
import { StoredCacheHandler } from '/@services/stored-cache-service/stored-cache-handler';
import {
    FLAGS,
    flags,
    initFlags,
} from '/@services/system/config-service/flags-service';
import { initSessionService } from '/@services/system/config-service/session-service';
import { setHostEventConfig } from '/@utils/event-bridge/core/utils';
import { PERF_EVENT, PERF_KEYS, setPerfLog } from '/@utils/perf-log';
import { client } from '../../../apollo-client';
import { resetAllPrivileges } from '../../../routing/privilege-list';
import { startWebStorage } from '../config-service/web-storage-service';
import { useUpdateSessionInfo } from '../login-service';
import {
    exposeStringsStyleOverrider,
    initializeCustomCalendars,
    initializeEmbedCustomerGuideScript,
    initializeLocaleFormats,
    initializeThirdPartyServices,
    initializeTooltip,
    notifySDKOnAuth,
    setEmbedInitData,
    stringOverrideLoader,
} from './lifecycle-hooks-utils';

/* all async calls which should finish before UI render.
 * If something is important for UI to render, would also be
 * important for some of the other services to get initialized.
 * Thus calling this method before anything else during initialization.
 */
export async function preAppRenderAsync() {
    // Read before editing
    // The function calls which are independent and can be made in parallel
    // should go inside following Promise.all block.
    // Only those function Calls which are dependent and cannot be executed
    // in parallel should be added after Promise.all block
    // in a separate await statement.
    initFlags();
    const exposeTranslationIDs =
        flags.getValue(FLAGS.exposeTranslationIDs.name) || false;
    setPerfLog(PERF_KEYS.SESSION_INFO_UPDATE, PERF_EVENT.START);
    await Promise.all([useUpdateSessionInfo(), startWebStorage()]);
    setPerfLog(PERF_KEYS.SESSION_INFO_UPDATE, PERF_EVENT.COMPLETE);
    await initializeAppLocale((clientLocale?: string) => {
        return getFormatLocales(clientLocale, isLocalizationEnabled());
    }, exposeTranslationIDs);
    setPerfLog(PERF_KEYS.INITIALIZE_APP_LOCALE_COMPLETE, PERF_EVENT.COMPLETE);
    overrideStrings();
}

/* contain function calls which are either
 * 1. Synchronous
 * 2. Async calls which are not required to finish before UI render
 */
export function preAppRenderSync() {
    // please do not perform computationally intensive tasks here
    // as it will block the initial render and make app slow to load.
    initializeTooltip();
    initLogger({
        debug: flags.getValue(FLAGS.debug?.name),
        clientLogLevel: flags.getValue(FLAGS.clientLogLevel?.name),
        overrideConsoleLogs: flags.getValue(FLAGS.overrideConsoleLogs?.name),
    });
}

/* all function calls which are not necessarily required
 * before App render can be called here.
 */
export async function postInitialAppRender() {
    logMicroFrontendsDebugInfo();

    if (
        getLiveboardVizDataCacheConfig().enable ||
        getMuzeChartIdbCacheEnabled()
    ) {
        // Start periodic cleanup of expired cache entries
        StoredCacheHandler.startPeriodicCleanup();
    }
}

interface PostAuthenticationOptions {
    /** Flag indicating if pre-authentication info was updated */
    preauthInfoUpdated?: boolean;
}

/* following lifecycle hook will be called:
 * 1. On fresh login
 * 2. When session is already authenticated, and user
 *    opens the app in a new tab.
 * 3. When service worker sees difference in preauth info, and sends a message to the app
 */
export async function postAuthentication(
    sessionInfo: any,
    options: PostAuthenticationOptions = {},
) {
    const { preauthInfoUpdated = false } = options;
    await initSessionService(sessionInfo);
    resetAllPrivileges();
    const exposeTranslationIDs =
        flags.getValue(FLAGS.exposeTranslationIDs.name) || false;
    exposeStringsStyleOverrider(exposeTranslationIDs);
    await initializeAppLocale(getFormatLocales, exposeTranslationIDs);
    if (isAutomaticTranslation()) initializeChromiumTranslate();
    if (isManualTranslation()) fetchAndRegisterI18nTranslations();
    // Notify SDK with user details. Needs to be after sessionService init
    notifySDKOnAuth();
    const { dateFormatLocale, numberFormatLocale } = getFormatLocales();

    setPerfLog(PERF_KEYS.INITIALISE_LOCALE_FORMATS, PERF_EVENT.START);
    const initialiseLocaleFormatsPromise = initializeLocaleFormats(
        dateFormatLocale,
        numberFormatLocale,
        getPreferredCurrencyFormat(),
    );

    await Promise.all([
        initialiseLocaleFormatsPromise.then(result => {
            setPerfLog(
                PERF_KEYS.INITIALISE_LOCALE_FORMATS,
                PERF_EVENT.COMPLETE,
            );
            return result;
        }),
        initializeCustomCalendars(),
        !preauthInfoUpdated && client.resetStore(),
    ]);
    if (isMixpanelEnabled(sessionInfo.configInfo)) {
        setMixpanelConfig(sessionInfo.configInfo.mixpanelConfig);
    }

    initializeEmbedCustomerGuideScript();
    initializeThirdPartyServices();
    onUserLoggedIn.publish();
}

/* following lifecycle hook is called on logout
 */
export async function postLogout() {
    resetAllPrivileges();
    markManuallyLoggedOut();
    onUserLoggedOut.publish();
    await Promise.all([client.clearStore(), initSessionService(null)]);
}

export async function onEmbedInit(data: any) {
    const stringOverrides = data?.customisations?.content?.strings;
    const stringIDOverrides = data?.customisations?.content?.stringIDs;
    setStringOverrides(stringOverrides);
    setHostEventConfig({
        shouldBypassPayloadValidation: data?.shouldBypassPayloadValidation,
        useHostEventsV2: data?.useHostEventsV2 ?? false,
    });
    const stringOverridesObj = await stringOverrideLoader(
        data?.customisations?.content?.stringIDsUrl,
    );
    setTranslationIDOverrides(_.merge(stringIDOverrides, stringOverridesObj));
    await setEmbedInitData(data);
}
